import { createFileRoute, Link } from "@tanstack/react-router";
import { MATERIALS_BASE_URL } from "@/lib/config";
import { ACBackground } from "@/components/ACBackground";
import { SponsoredBy } from "@/components/SponsoredBy";

export const Route = createFileRoute("/schedule")({
  head: () => ({
    meta: [
      { title: "Satellite School Agenda — August 26, 2026 · Mexico City" },
      {
        name: "description",
        content:
          "Full one-day agenda of the Matter Lab Satellite School: parallel tracks on AI Agents and Bayesian Optimization & Self-Driving Labs, with theory and hands-on sessions.",
      },
      { property: "og:title", content: "Satellite School Agenda — August 26, 2026" },
      {
        property: "og:description",
        content:
          "Two parallel tracks, theory and hands-on: AI Agents and Bayesian Optimization & Self-Driving Labs at Centro de Ciencias de la Complejidad, UNAM.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SatelliteSchoolSchedule,
});

type Slot = {
  time: string;
  full?: string | { title: string; instructors?: string };
  s1?: { title: string; instructors?: string };
  s2?: { title: string; instructors?: string };
};

const slots: Slot[] = [
  { time: "08:30 – 09:00", full: "Registration" },
  {
    time: "09:00 – 10:25",
    s1: { title: "Theory: Introduction to AI and Agents", instructors: "Austin, Andy, Manav, Jiaru, Nacho" },
    s2: { title: "Theory: Introduction to BO and SDLs", instructors: "Marcel M, Kourosh, Felix, Jesús" },
  },
  { time: "10:25 – 10:35", full: "Hydration break (10 min)" },
  {
    time: "10:35 – 12:00",
    s1: { title: "Hands-on session for agents with El Agente", instructors: "Andy, Yuma, Carolina, Jiaru, Juan" },
    s2: { title: "Hands-on session for BO and SDLs", instructors: "Jesús, Felix, Kourosh, Luis" },
  },
  { time: "12:00 – 13:30", full: "Lunch" },
  {
    time: "13:30 – 14:55",
    s1: { title: "Swap · Theory: Introduction to BO and SDLs" },
    s2: { title: "Swap · Theory: Introduction to AI and Agents" },
  },
  { time: "14:55 – 15:05", full: "Hydration break (10 min)" },
  {
    time: "15:05 – 16:30",
    s1: { title: "Swap · Hands-on session for BO and SDLs" },
    s2: { title: "Swap · Hands-on session for AI and Agents" },
  },
  { time: "16:30 – 16:40", full: "Hydration break (10 min)" },
  {
    time: "16:40 – 18:00",
    full: { title: "Advanced", instructors: "Kourosh, Luis, Jesús, Felix" },
  },
];

function SatelliteSchoolSchedule() {
  return (
    <main className="relative pt-32 pb-24">
      <ACBackground />
      <div className="relative z-10 mx-auto max-w-5xl px-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">Matter Lab International Conference 2026 ∙ Satellite School</div>
        <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-foreground">Agenda</h1>
        <p className="mt-6 text-lg text-muted-foreground">
          August 26, 2026 · Centro de Ciencias de la Complejidad, UNAM. Two parallel tracks run in the morning and swap
          after lunch, so every participant sees both. Subject to change.
        </p>

        <div className="mt-10 hidden sm:grid grid-cols-[9rem_1fr_1fr] gap-4 text-sm uppercase tracking-widest text-muted-foreground font-bold">
          <div className="text-center">Time</div>
          <div className="text-center">Session 1</div>
          <div className="text-center">Session 2</div>
        </div>

        <div className="mt-3 space-y-3">
          {slots.map((slot) => (
            <div
              key={slot.time}
              className="rounded-2xl border border-border bg-card p-5 sm:grid sm:grid-cols-[9rem_1fr_1fr] sm:gap-4 sm:items-start"
            >
              <div className="font-mono text-sm text-muted-foreground text-center">{slot.time}</div>
              {slot.full ? (
                <div className="mt-2 sm:mt-0 sm:col-span-2 text-foreground text-center">
                  {typeof slot.full === "string" ? (
                    slot.full
                  ) : (
                    <>
                      <div>{slot.full.title}</div>
                      {slot.full.instructors && (
                        <div className="mt-1 text-sm text-muted-foreground">Instructors: {slot.full.instructors}</div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {[slot.s1, slot.s2].map((s, i) => (
                    <div key={i} className="mt-3 sm:mt-0 text-center">
                      <div className="text-foreground">{s?.title}</div>
                      {s?.instructors && (
                        <div className="mt-1 text-sm text-muted-foreground">Instructors: {s.instructors}</div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-20">
          <p className="text-lg text-foreground mb-8">Slides, notebooks and reference material.</p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <a
              href={`${MATERIALS_BASE_URL}/ai-agents`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between rounded-2xl border border-border bg-card px-8 py-7 transition-colors hover:border-salmon/50"
            >
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">AI Agents</h2>
                <p className="mt-1 text-sm text-muted-foreground">Track materials</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-salmon transition-colors group-hover:border-salmon/50 group-hover:bg-salmon/10">
                <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </a>
            <a
              href={`${MATERIALS_BASE_URL}/self-driving-labs`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between rounded-2xl border border-border bg-card px-8 py-7 transition-colors hover:border-salmon/50"
            >
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Self-Driving Labs</h2>
                <p className="mt-1 text-sm text-muted-foreground">Track materials</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-salmon transition-colors group-hover:border-salmon/50 group-hover:bg-salmon/10">
                <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </a>
            <a
              href={`${MATERIALS_BASE_URL}/advanced`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between rounded-2xl border border-border bg-card px-8 py-7 transition-colors hover:border-salmon/50"
            >
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Advanced</h2>
                <p className="mt-1 text-sm text-muted-foreground">Track materials</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-salmon transition-colors group-hover:border-salmon/50 group-hover:bg-salmon/10">
                <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </a>
          </div>
        </div>

        <Link
          to="/"
          className="mt-12 inline-block text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to Satellite School
        </Link>
        <SponsoredBy />
      </div>
    </main>
  );
}
